import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { adminGroups, adminCheckinLive } from "@/lib/api";
import { CHECKIN_FORM, localize } from "@/data/part2Content";

const todayIso = () => new Date().toISOString().slice(0, 10);

function MoodBar({ people }) {
  const moods = people.map((p) => Number(p.answers?.mood)).filter((m) => m >= 1 && m <= 5);
  const avg = moods.length ? (moods.reduce((a, b) => a + b, 0) / moods.length).toFixed(1) : "–";
  const max = Math.max(1, ...[1, 2, 3, 4, 5].map((n) => moods.filter((m) => m === n).length));
  return (
    <div className="rbl-live-mood" data-testid="live-mood">
      <div><div className="rbl-live-avg" data-testid="live-mood-avg">{avg}</div><div className="rbl-live-lbl">/ 5</div></div>
      <div className="rbl-live-bars">
        {[1, 2, 3, 4, 5].map((n) => {
          const c = moods.filter((m) => m === n).length;
          return (
            <div key={n} className="rbl-live-bar" data-testid={`live-mood-${n}`}>
              <div className="fill" style={{ height: `${(c / max) * 100}%` }}><span>{c || ""}</span></div>
              <div className="n">{n}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminCheckinLive() {
  const { lang } = useApp();
  const form = useMemo(() => localize(CHECKIN_FORM, lang), [lang]);
  const [groups, setGroups] = useState([]);
  const [gid, setGid] = useState("");
  const [day, setDay] = useState(1);
  const [data, setData] = useState(null);

  useEffect(() => {
    adminGroups().then((gs) => {
      const p2 = gs.filter((g) => g.part2);
      setGroups(p2);
      const today = p2.find((g) => (g.part2_dates || []).includes(todayIso()));
      const g = today || p2[0];
      if (g) { setGid(g.id); setDay(today ? g.part2_dates.indexOf(todayIso()) + 1 : 1); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!gid) return undefined;
    const load = () => adminCheckinLive(gid, day).then(setData).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [gid, day]);

  const people = data?.participants || [];
  const done = people.filter((p) => p.answers);
  const open = people.filter((p) => !p.answers);
  const textFields = form.fields.filter((f) => f.type === "text");

  return (
    <div className="rbl rbl-live" data-testid="checkin-live-page">
      <div className="rbl-live-top">
        <Link to="/admin/part2" className="rbl-back" data-testid="live-back">← Admin</Link>
        <select className="rbl-input" style={{ width: "auto" }} value={gid} onChange={(e) => setGid(e.target.value)} data-testid="live-group">
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <select className="rbl-input" style={{ width: "auto" }} value={day} onChange={(e) => setDay(Number(e.target.value))} data-testid="live-day">
          {[1, 2, 3, 4].map((d) => <option key={d} value={d}>Day {d}</option>)}
        </select>
        <span className="rbl-live-count" data-testid="live-count">{done.length} / {people.length} ✓</span>
      </div>
      <h1 className="rbl-h1" style={{ fontSize: 44, marginTop: 10 }}>Check-in · {data?.group || ""}</h1>
      <div className="rbl-live-grid">
        <div className="rbl-feature">
          <h3>{form.fields[0].label}</h3>
          <MoodBar people={done} />
          <div className="rbl-live-names">
            {done.map((p) => <span key={p.id} className="rbl-live-chip" data-testid={`live-person-${p.id}`}>{p.name} · {p.answers.mood ?? "–"}</span>)}
          </div>
          {open.length > 0 && <p className="rbl-live-open" data-testid="live-open">… {open.map((p) => p.name).join(", ")}</p>}
        </div>
        {textFields.map((f) => (
          <div className="rbl-feature" key={f.id} data-testid={`live-q-${f.id}`}>
            <h3>{f.label}</h3>
            {done.filter((p) => (p.answers[f.id] || "").trim()).map((p) => (
              <div key={p.id} className="rbl-live-answer"><strong>{p.name}</strong><span>{p.answers[f.id]}</span></div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
